import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, useAuth } from '../../firebase';

function getNextOrderForStatus(todos, statusId) {
  const inStatus = todos.filter((t) => t.status === statusId);
  const max = inStatus.reduce((acc, t) => Math.max(acc, Number.isFinite(t.order) ? t.order : 0), 0);
  return max + 1000;
}

function useTodos(companyId) {
  const currentUser = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const todosRef = useMemo(() => {
    if (!companyId) return null;
    return collection(db, 'user_data', companyId, 'todos');
  }, [companyId]);

  useEffect(() => {
    if (!todosRef) {
      setTodos([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const unsub = onSnapshot(
      todosRef,
      (snapshot) => {
        setTodos(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError(err);
        setLoading(false);
      }
    );

    return unsub;
  }, [todosRef]);

  const createTodo = useCallback(
    async ({ name, description, category, assignee, status }) => {
      if (!todosRef) return;
      try {
        setError(null);
        const order = getNextOrderForStatus(todos, status);
        await addDoc(todosRef, {
          name: name?.trim() || '',
          description: description?.trim() || '',
          category: category || '',
          assignee: assignee || null,
          status,
          order,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: currentUser?.uid || null,
        });
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [todosRef, todos, currentUser]
  );

  const updateTodo = useCallback(
    async (todoId, patch) => {
      if (!companyId || !todoId) return;
      try {
        setError(null);
        await updateDoc(doc(db, 'user_data', companyId, 'todos', todoId), {
          ...patch,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId]
  );

  const deleteTodo = useCallback(
    async (todoId) => {
      if (!companyId || !todoId) return;
      try {
        setError(null);
        await deleteDoc(doc(db, 'user_data', companyId, 'todos', todoId));
      } catch (err) {
        console.error(err);
        setError(err);
        throw err;
      }
    },
    [companyId]
  );

  const moveTodo = useCallback(
    async (todoId, { status, order }) => {
      if (!companyId || !todoId) return;
      return updateTodo(todoId, { status, order });
    },
    [companyId, updateTodo]
  );

  return { todos, loading, error, createTodo, updateTodo, deleteTodo, moveTodo };
}

export default useTodos;

